// Governance Override 상태 머신 훅.
//
// 권위:
//   - ARCH_OBSERVABILITY.md — `/topic/governance` 채널 + GOVERNANCE.OVERRIDE.* 이벤트.
//   - ARCH_DASHBOARD.md v1.7 §4.0 GlobalOverrideBanner.
//
// 구독하는 이벤트 코드:
//   - GOVERNANCE.OVERRIDE.RAPID_DROP_TRIGGERED   → rapidDrop.active = true
//   - GOVERNANCE.OVERRIDE.RAPID_DROP_RECOVERED   → rapidDrop.active = false
//   - GOVERNANCE.OVERRIDE.VOLATILITY_EXPANSION_TRIGGERED
//   - GOVERNANCE.OVERRIDE.VOLATILITY_EXPANSION_RECOVERED
//
// 초기 상태: 앱 마운트 시 GET /api/v1/data/governance-history?override_only=true&size=5
// 로 최근 이벤트를 읽어와 현재 active 여부를 복구. 백엔드 미구현 상태면 조용히 무시
// (active=false 로 시작). 실패해도 banner 가 렌더 자체를 막지 않음.

import { useCallback, useEffect, useState } from 'react';
import type { IMessage } from '@stomp/stompjs';
import { subscribe } from './useWebSocket';
import { apiGet } from '../api/client';

export interface OverrideStatus {
  active: boolean;
  triggeredAt: string | null;
  reason: string | null;
}

export interface GovernanceOverrideState {
  rapidDrop: OverrideStatus;
  volatility: OverrideStatus;
  /** 세션 로컬 수동 dismiss — STOMP 메시지 재수신 시 되살아남. */
  dismissed: boolean;
}

const INITIAL: GovernanceOverrideState = {
  rapidDrop: { active: false, triggeredAt: null, reason: null },
  volatility: { active: false, triggeredAt: null, reason: null },
  dismissed: false,
};

// STOMP payload — 이벤트 서브젝트(event_code) + reason/at 필드.
interface GovernancePayload {
  event_code?: string;
  eventCode?: string;
  triggered_at?: string;
  triggeredAt?: string;
  recovered_at?: string;
  reason?: string;
}

// 초기 로드용 — /api/v1/data/governance-history 응답 아이템.
interface GovernanceHistoryRow {
  tradingDate?: string;
  overrideRapidDropActive?: boolean;
  overrideRapidDropReason?: string | null;
  overrideRapidDropTriggeredAt?: string | null;
  overrideVolatilityActive?: boolean;
  overrideVolatilityReason?: string | null;
  overrideVolatilityTriggeredAt?: string | null;
}

interface GovernanceHistoryList {
  items: GovernanceHistoryRow[];
  total: number;
}

function pickCode(p: GovernancePayload): string {
  return (p.event_code ?? p.eventCode ?? '').toUpperCase();
}

function pickAt(p: GovernancePayload): string | null {
  return p.triggered_at ?? p.triggeredAt ?? p.recovered_at ?? null;
}

export function useGovernanceOverride(): {
  state: GovernanceOverrideState;
  dismiss: () => void;
  anyActive: boolean;
} {
  const [state, setState] = useState<GovernanceOverrideState>(INITIAL);

  // 초기 스냅샷 로드 — 실패 시 조용히 무시.
  useEffect(() => {
    let cancelled = false;
    apiGet<GovernanceHistoryList>(
      '/api/v1/data/governance-history?override_only=true&size=1',
    )
      .then((resp) => {
        if (cancelled) return;
        const row = resp.items?.[0];
        if (!row) return;
        setState((prev) => ({
          ...prev,
          rapidDrop: {
            active: Boolean(row.overrideRapidDropActive),
            triggeredAt: row.overrideRapidDropTriggeredAt ?? null,
            reason: row.overrideRapidDropReason ?? null,
          },
          volatility: {
            active: Boolean(row.overrideVolatilityActive),
            triggeredAt: row.overrideVolatilityTriggeredAt ?? null,
            reason: row.overrideVolatilityReason ?? null,
          },
        }));
      })
      .catch(() => {
        // 백엔드 미구현 또는 네트워크 오류 — active=false 로 유지.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // STOMP 구독 — /topic/governance.
  useEffect(() => {
    const unsub = subscribe('/topic/governance', (msg: IMessage) => {
      let payload: GovernancePayload | null = null;
      try {
        payload = JSON.parse(msg.body) as GovernancePayload;
      } catch {
        return;
      }
      if (!payload) return;
      const code = pickCode(payload);
      const at = pickAt(payload);
      const reason = payload.reason ?? null;

      setState((prev) => {
        switch (code) {
          case 'GOVERNANCE.OVERRIDE.RAPID_DROP_TRIGGERED':
            return {
              ...prev,
              rapidDrop: { active: true, triggeredAt: at, reason },
              dismissed: false,
            };
          case 'GOVERNANCE.OVERRIDE.RAPID_DROP_RECOVERED':
            return {
              ...prev,
              rapidDrop: { active: false, triggeredAt: at, reason },
            };
          case 'GOVERNANCE.OVERRIDE.VOLATILITY_EXPANSION_TRIGGERED':
            return {
              ...prev,
              volatility: { active: true, triggeredAt: at, reason },
              dismissed: false,
            };
          case 'GOVERNANCE.OVERRIDE.VOLATILITY_EXPANSION_RECOVERED':
            return {
              ...prev,
              volatility: { active: false, triggeredAt: at, reason },
            };
          default:
            return prev;
        }
      });
    });
    return unsub;
  }, []);

  const dismiss = useCallback(() => {
    setState((prev) => ({ ...prev, dismissed: true }));
  }, []);

  const anyActive = state.rapidDrop.active || state.volatility.active;

  return { state, dismiss, anyActive };
}
